import { useFieldToolsStore } from "../../stores/fieldToolsStore";
import { useRxEdit } from "../../stores/rxEditStore";
import { useUiStore } from "../../stores/uiStore";
import {
  rectAnchor,
  useWizard,
  type WizardDef,
  type WizardStep,
} from "./wizardStore";

/* * anchor by an element's aria-label (the sidebar rows' stable handle) */
const byAria = (re: RegExp) => (): HTMLElement | null => {
  for (const el of document.querySelectorAll<HTMLElement>("[aria-label]")) {
    if (re.test(el.getAttribute("aria-label") ?? "")) return el;
  }
  return null;
};
/* * anchor by selector (buttons carry data-wizard-anchor; panels their testids) */
const bySel = (sel: string) => (): HTMLElement | null =>
  document.querySelector<HTMLElement>(sel);

/* * does a click land on (or inside) an element matching the selector / aria regex? */
const clickSel =
  (sel: string) =>
  (el: HTMLElement): boolean =>
    el.closest(sel) != null;
const clickAria =
  (re: RegExp) =>
  (el: HTMLElement): boolean => {
    const hit = el.closest<HTMLElement>("[aria-label]");
    return hit != null && re.test(hit.getAttribute("aria-label") ?? "");
  };

// aria matchers for the sidebar rows the tour anchors on / advances from
const COUNTY_ROW = /county/i;
// ^-anchored to the SAMPLES rows ('Organic matter (samples)' / '· 8-inch normalized') — a bare
// /CEC/ or /organic matter/ also hit the Map layers rows ('Soil CEC (modeled)'), which render FIRST
// in the DOM and stole the anchor.
const OM_ROW = /^Organic matter/i;
const CEC_ROW = /^CEC/i;
const ML_P_ROW = /^Soil P \(modeled\)/;

// THE POSITION PASS: widget anchors are now MAP GEOMETRY where he asked for it (field boundary
// rightmost compare ring, rightmost AI-win star, the pane divider) via registered rect providers
// the pulsing ring stays on the CLICK TARGET (`highlight`) so “what do I press?
const FIELD_BOUNDARY = rectAnchor("map.fieldBoundary");
const COMPARE_RIGHT = rectAnchor("split.compareRight");
const SPLIT_DIVIDER = rectAnchor("split.divider");
const CHECKACC_DIVIDER = rectAnchor("checkacc.divider");
const AI_WIN_STAR = rectAnchor("checkacc.aiWinStar");

const steps: WizardStep[] = [
  {
    // STEP 0 — UNIQUE: the lesson-plan loader. WizardHost renders the robot-at-the-chalkboard
    // modal, runs the headless holdout check (AI wins P AND ≥2 other nutrients), then advances
    // automatically or offers X / "Continue Anyways".
    id: "lesson-plan",
    scene: null,
    text: () => "Preparing lesson plan…",
    next: false,
  },
  {
    id: "k-samples",
    scene: {
      solo: { kind: "samples", prop: "k" },
      split: null,
      checkAccuracy: null,
    },
    anchor: FIELD_BOUNDARY, // “just to the left of the field boundary on the map”
    side: "left",
    highlight: bySel('[data-wizard-anchor="create-split"]'),
    text: ({ fieldName }) =>
      `This is a simple map of ${fieldName}’s K levels from your soil samples. But it’s missing a lot of field variation. What is it missing?`,
    advance: {
      match: clickSel('[data-wizard-anchor="create-split"]'),
      passthrough: true,
    },
    next: true,
  },
  {
    id: "k-vs-twi",
    scene: {
      split: {
        left: { kind: "samples", prop: "k" },
        right: { kind: "covariate", match: "^topographic wetness index" },
        compare: true,
      },
      checkAccuracy: null,
      sidebar: { query: "county" },
    },
    anchor: COMPARE_RIGHT, // “just to the right of the right-most compare cluster”
    side: "right",
    highlight: byAria(COUNTY_ROW),
    text: () =>
      "The map on the right shows how moisture flows through the field based on elevation. K levels are usually closely associated with these patterns, but these hills and waterways aren’t showing up on the K map. What else is missing?",
    advance: { match: clickAria(COUNTY_ROW), passthrough: false },
    next: true,
  },
  {
    id: "k-vs-productivity",
    scene: {
      split: {
        left: { kind: "samples", prop: "k" },
        right: {
          kind: "covariate",
          match: "productivity.*county|county.*(%|pct|tile)",
        },
        compare: true,
      },
      checkAccuracy: null,
      sidebar: { query: "", openGroups: ["__soil__"] },
    },
    anchor: COMPARE_RIGHT,
    side: "right",
    highlight: byAria(OM_ROW),
    text: () =>
      "K levels also track well with productivity, but the K map misses these areas, too. Let’s look at other nutrients.",
    advance: { match: clickAria(OM_ROW), passthrough: false },
    next: true,
  },
  {
    id: "om-vs-baresoil",
    scene: {
      split: {
        left: { kind: "samples", prop: "om" },
        right: { kind: "covariate", match: "bare soil" },
        compare: true,
      },
      checkAccuracy: null,
      sidebar: { openGroups: ["__soil__"] },
    },
    anchor: COMPARE_RIGHT,
    side: "right",
    highlight: byAria(CEC_ROW),
    text: () =>
      "Organic Matter should compare well with the bare soil color. And in some places it does! But not everywhere. Try another nutrient?",
    advance: { match: clickAria(CEC_ROW), passthrough: false },
    next: true,
  },
  {
    id: "cec-vs-mapunit",
    scene: {
      split: {
        left: { kind: "samples", prop: "cec" },
        right: { kind: "covariate", match: "map unit|soil map|mukey" },
        compare: true,
      },
      checkAccuracy: null,
      // the anchor row (Soil P (modeled)) lives in the Map layers folder — make sure it's open
      sidebar: { openGroups: ["__map__"] },
    },
    anchor: COMPARE_RIGHT,
    side: "right",
    highlight: byAria(ML_P_ROW),
    text: () =>
      "The samples provide GREAT data, but they don’t change when the soil type changes, even though it probably does in the real world. Think there’s a better way to map soil?",
    advance: { match: clickAria(ML_P_ROW), passthrough: false },
    next: true,
  },
  {
    id: "ai-map",
    scene: {
      solo: { kind: "id", id: "ml-p" },
      split: null,
      checkAccuracy: null,
    },
    anchor: FIELD_BOUNDARY, // “just to the right of the field boundary on the map”
    side: "right",
    highlight: bySel('[data-wizard-anchor="check-accuracy"]'),
    text: () =>
      "AI can use these layers (and many others) to create better maps — here’s what they look like. But how do we know if they’re more accurate?",
    advance: {
      match: clickSel('[data-wizard-anchor="check-accuracy"]'),
      passthrough: true,
    },
    next: true,
  },
  {
    id: "checkacc-open",
    scene: {
      checkAccuracy: {
        prop: "p",
        holdout: false,
        mistakes: false,
        panel: false,
      },
    },
    anchor: CHECKACC_DIVIDER, // “in the middle of the split view, between the two maps”
    side: "over",
    highlight: bySel('[data-wizard-anchor="holdout-toggle"]'),
    text: () =>
      "Here’s the AI Map, compared to a regular one. The AI Map catches trends that regular maps miss. But how do we know which is better? Let’s remove some test samples and see how well each map does at predicting their values.",
    // SUPPRESSED: the button's native click runs an UNTUNED manual holdout (μ stays wherever the
    // slider sits) and raced the driver's tuned run into 2-3 concurrent EBK solves.
    advance: {
      match: clickSel('[data-wizard-anchor="holdout-toggle"]'),
      passthrough: false,
    },
    next: true,
  },
  {
    id: "holdout",
    scene: {
      checkAccuracy: { prop: "p", holdout: true, mistakes: false, panel: true },
    },
    anchor: AI_WIN_STAR, // “next to the right-most star with a green circle — the AI won there”
    highlight: bySel('[data-wizard-anchor="spreading-mistakes"]'),
    text: () =>
      "The stars are samples that were held out of the mapping process. We can see which map was closer at those withheld samples — AI or the normal map. The panel on the right tells you at how many samples the AI map was better, worse, and the same. If it was better more often than it was worse, we’ve got a good map! But why does this matter?",
    advance: {
      match: clickSel('[data-wizard-anchor="spreading-mistakes"]'),
      passthrough: true,
    },
    next: true,
  },
  {
    id: "mistakes",
    scene: {
      checkAccuracy: { prop: "p", holdout: true, mistakes: true, panel: true },
    },
    anchor: bySel('[data-testid="mistake-legend"]'), // “next to the red/green/purple legend”
    text: () =>
      "This map shows you places where we’d be spreading too much or not enough DAP on this field because of the inaccurate map. And the box on the right tells you how many pounds of DAP and other products we’d be misapportioning. By switching to the AI Map, you’ll save money in the purple areas, and avoid nutrient deficiencies in the red areas.",
    next: true,
  },
  {
    id: "economic-value",
    scene: {
      checkAccuracy: { prop: "p", holdout: true, mistakes: true, panel: true },
    },
    anchor: bySel('[data-testid="econ-value-panel"]'),
    text: () =>
      "Using standard rates, this estimates the value of the products we’d be reapportioning. This is a combination of direct cost savings, and ways to increase yield potential through fertility. Want to look at some other ways we can improve our Rx?",
    next: true,
  },
  {
    id: "rx-open",
    // the K GENIUS session opens (the driver fetches inputs + begins it); Check Accuracy closes
    scene: {
      rxEditor: { presetId: "k-genius", section: null },
      split: null,
      checkAccuracy: null,
    },
    anchor: FIELD_BOUNDARY, // “just to the right of the field boundary on the map”
    side: "right",
    highlight: bySel('[data-wizard-anchor="rx-section-genius"]'),
    text: () =>
      "These are our Elemental K Rx. They use our AI Maps, and make some additional adjustments using your field’s data. Wonder what they are?",
    advance: {
      match: clickSel('[data-wizard-anchor="rx-section-genius"]'),
      passthrough: true,
    },
    next: true,
  },
  {
    id: "rx-genius-menu",
    scene: {
      rxEditor: { presetId: "k-genius", section: "genius" },
      checkAccuracy: null,
    },
    anchor: bySel('[data-wizard-anchor="rx-slider-diffusionPct"]'), // “left of the diffusion slider”
    side: "left",
    highlight: bySel('[data-wizard-anchor="rx-compare-traditional"]'),
    text: () =>
      "K is more or less available in different areas based on things like topographic waterflow and soil type. By accounting for these, we can make our recommendations even smarter.",
    advance: {
      match: clickSel('[data-wizard-anchor="rx-compare-traditional"]'),
      passthrough: true,
    },
    next: true,
  },
  {
    id: "rx-vs-traditional",
    scene: {
      rxEditor: {
        presetId: "k-genius",
        section: "genius",
        compareTraditional: true,
        compareOn: false,
      },
      checkAccuracy: null,
    },
    anchor: SPLIT_DIVIDER, // “in the middle of the split view, between the two maps”
    side: "over",
    highlight: bySel('[data-wizard-anchor="split-compare-toggle"]'),
    text: () =>
      "This is our Genius Rx based on the AI map, next to the Traditional Rx without the AI map. Hover over the map to see the differences. What makes the Genius Rx better? Click ‘Compare’ to find out.",
    advance: {
      match: clickSel('[data-wizard-anchor="split-compare-toggle"]'),
      passthrough: true,
    },
    next: true,
  },
  {
    id: "rx-diffusion",
    // cause 2 = Diffusion circled; the advance is the Clay fixation chip (cause 4)
    scene: {
      rxEditor: {
        presetId: "k-genius",
        section: "genius",
        compareTraditional: true,
        compareOn: true,
        rxCat: 2,
      },
      checkAccuracy: null,
    },
    anchor: COMPARE_RIGHT, // “right of the right-most compare cluster”
    side: "right",
    highlight: bySel('[data-wizard-anchor="rx-cause-4"]'),
    text: () =>
      "Based on topography and soil type, our diffusion model adjusts the recs up or down in these areas. More diffusion means less fertilizer is needed than the Traditional Rx supplies, and vice versa. We do the same for Clay Fixation.",
    advance: {
      match: clickSel('[data-wizard-anchor="rx-cause-4"]'),
      passthrough: true,
    },
    next: true,
  },
  {
    id: "rx-clay-fixation",
    scene: {
      rxEditor: {
        presetId: "k-genius",
        section: "genius",
        compareTraditional: true,
        compareOn: true,
        rxCat: 4,
      },
      checkAccuracy: null,
    },
    anchor: COMPARE_RIGHT,
    side: "right",
    highlight: bySel('[data-wizard-anchor="rx-section-rates"]'),
    text: () =>
      "We do the same thing with Clay Fixation. Certain types of clay ‘fix’ more K in the soil - making it unavailable to the plant. The Genius Rx finds these areas and compensates for them in the K Rx.",
    advance: {
      match: clickSel('[data-wizard-anchor="rx-section-rates"]'),
      passthrough: true,
    },
    next: true,
  },
  {
    id: "rx-budget-save",
    // the comparison closes, the final-adjustments menu opens; D (Save Rx) is SWALLOWED — the
    // advance saves programmatically as 'My First K Rx' instead of opening the naming input
    scene: {
      rxEditor: {
        presetId: "k-genius",
        section: "rates",
        compareTraditional: false,
      },
      split: null,
      checkAccuracy: null,
    },
    anchor: bySel('[data-wizard-anchor="rx-budget"]'), // “left of the ‘spread this much’ box”
    side: "left",
    highlight: bySel('[data-wizard-anchor="rx-save"]'),
    text: () =>
      "We all get that there are budget constraints. If you want to scale the overall fertilizer rate up or down, you can use this smart adjustment to add more or less in the areas where it will have the most impact. Go ahead and give it a try.",
    advance: {
      match: clickSel('[data-wizard-anchor="rx-save"]'),
      passthrough: false,
    },
    onAdvance: () => {
      void useRxEdit.getState().save("My First K Rx");
    },
    next: true,
  },
  {
    id: "spread-menu",
    // DASHBOARD from here: the host reconciler closes the map, ends the Rx session the tour opened
    // and opens the Spread Fertilizer panel.
    scene: { view: "dashboard", rxEditor: null, spread: { editing: null } },
    anchor: bySel('[data-wizard-anchor="spread-customize-spread-mop"]'),
    text: () =>
      "Now that we have the elemental Rx, we can download a spread map for our chosen product. Pick ‘Muriate of Potash’ here, as an example, and customize it.",
    advance: {
      match: clickSel('[data-wizard-anchor="spread-customize-spread-mop"]'),
      passthrough: true,
    },
    next: true,
  },
  {
    id: "spread-pdf",
    scene: { view: "dashboard", spread: { editing: "spread-mop" } },
    anchor: bySel('[data-wizard-anchor="spread-download-pdf"]'),
    text: () =>
      "Based on our product, we can specify the percent K2O, and generate our spread map. Generate a PDF report here to give it a look.",
    advance: {
      match: clickSel('[data-wizard-anchor="spread-download-pdf"]'),
      passthrough: true,
    },
    next: true,
  },
  {
    id: "finale",
    scene: { view: "dashboard", spread: { editing: "spread-mop" } },
    center: true,
    finale: true,
    text: () =>
      "Thanks for using this tutorial! Hopefully it was informative. Now take a look at the PDF you downloaded to review your spread map.",
    next: false, // the last step — X only
  },
];

export const learnAiModelWizard: WizardDef = { id: "learn-ai-model", steps };

/*
 *
 * The profile-menu entry point. Gate: a field must be selected ("select a field with high-res maps
 * already created before clicking"); with several selected, the FIRST is used. Opens the map for
 * that field and starts at step 0 (the lesson-plan check).
 */
export function startLearnAiModel(): void {
  const selection = useFieldToolsStore.getState().rowSelection;
  const fieldId = Object.keys(selection).find((id) => selection[id]) ?? null;
  if (!fieldId) {
    useWizard
      .getState()
      .setNotice(
        "Select a field with high-res maps already created, then click “Genius Rx Demo/Tutorial” again.",
      );
    return;
  }
  useRxEdit.getState().end();
  useUiStore.getState().openMap(fieldId);
  useWizard.getState().start(learnAiModelWizard, fieldId);
}
